import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { identity, pickBy } from 'lodash';
import { useSingleEffect } from 'react-haiku';
import { useCollect } from 'hooks';

import Show from 'components/Show';
import Progress from 'components/Misc/Progress';
import Nothing from 'components/Misc/Nothing';
import Pagination from 'components/Misc/Pagination';

import Search from 'containers/Administrator/Subjects/Profile/Courses/Search';
import Course from 'containers/Administrator/Subjects/Profile/Courses/Course';

const Courses = ({ tab, setTab }) => {
	// Store subject's id
	const { id } = useParams();

	// Update selected tab
	useSingleEffect(() => setTab(tab));

	// Store search data
	const [search, setSearch] = useState({
		title: '',
		page: 1
	});

	// Store request parameters
	const parameters = useMemo(() => pickBy({
		eager: 'subject',
		'title:likeLower': `%${search.title}%`,
		subjectId: id,
		page: search.page
	}, identity), [search, id]);

	// Store requested courses
	const [[collection, progress], collect] = useCollect('courses', parameters);

	// Handles search field's onChange event
	// @event: object | onChange event
	const onSearch = ({ name, value }) => {
		// Special case for page change
		if (name === 'page') return setSearch(prev => ({
			...prev,
			[name]: value
		}));

		setSearch(prev => ({
			...prev,
			[name]: value,
			page: 1
		}));
	};

	// Collect courses on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	return (
		<>
			{/* Search container */}
			<Search search={search} onChange={onSearch} />

			{/* Show loading progress */}
			<Progress progress={progress} />

			{/* Show nothing found */}
			<Nothing visible={!collection?.courses.length && !progress} />

			{/* Show collection if results collected */}
			<Show if={collection?.courses.length && !progress}>
				<div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{collection?.courses.map(course =>
						<Course key={course.id} course={course} />
					)}
				</div>
			</Show>

			{/* Collection's pagination */}
			<Pagination
				progress={progress}
				total={collection?.total}
				active={search.page}
				onChange={onSearch}
				className="mt-4 mx-auto"
			/>
		</>
	);
};

export default Courses;
